import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import { Cpu, Server, Key, Plus, CheckCircle, Clock, ShieldAlert, ArrowRight, Trash2 } from 'lucide-react';

const MT5Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: '', text: '' });

  // Delete modal state
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Form fields
  const [accountNumber, setAccountNumber] = useState('');
  const [investorPassword, setInvestorPassword] = useState('');
  const [server, setServer] = useState('');
  const [accountType, setAccountType] = useState('Standard');

  const fetchAccounts = async () => {
    try {
      const res = await axiosClient.get('/mt5/my-accounts');
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error('[MT5 Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountNumber || !investorPassword || !server) return;
    setSubmitting(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      await axiosClient.post('/mt5/submit', {
        accountNumber,
        investorPassword,
        server,
        accountType
      });
      setFeedbackMsg({ type: 'success', text: 'MT5 account submitted successfully for admin review!' });
      setAccountNumber('');
      setInvestorPassword('');
      setServer('');
      setShowSubmitModal(false);
      fetchAccounts();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit MT5 account.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;
    setDeleting(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      await axiosClient.delete(`/mt5/my-accounts/${accountToDelete._id}`);
      setFeedbackMsg({ type: 'success', text: `MT5 Account #${accountToDelete.accountNumber} deleted successfully.` });
      setAccountToDelete(null);
      fetchAccounts();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete MT5 account.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Workflow Banner */}
      <div className="bg-[#121215] border border-[#27272A] rounded-2xl p-5 shadow-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-extrabold text-[#FFFFFF] tracking-tight">MT5 Account Connection & Approval</h2>
            <p className="text-xs text-[#9CA3AF] mt-1">
              Submit your MT5 account details. Admin manually verifies credentials before activating copy trading via xalgos.in.
            </p>
          </div>
          <Button 
            variant="primary" 
            onClick={() => setShowSubmitModal(true)}
            className="shrink-0 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Connect MT5 Account</span>
          </Button>
        </div>

        {/* Workflow Steps Indicator */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-5 pt-4 border-t border-[#27272A]">
          <div className="p-3 bg-[#09090B] rounded-xl border border-[#27272A] flex items-center space-x-3">
            <span className="w-6 h-6 rounded-full bg-[#F97316]/20 text-[#F97316] font-bold text-xs flex items-center justify-center shrink-0">1</span>
            <span className="text-xs font-semibold text-[#FFFFFF]">Submit MT5 Details</span>
          </div>
          <div className="p-3 bg-[#09090B] rounded-xl border border-[#27272A] flex items-center space-x-3">
            <span className="w-6 h-6 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] font-bold text-xs flex items-center justify-center shrink-0">2</span>
            <span className="text-xs font-semibold text-[#FFFFFF]">Admin Verification</span>
          </div>
          <div className="p-3 bg-[#09090B] rounded-xl border border-[#27272A] flex items-center space-x-3">
            <span className="w-6 h-6 rounded-full bg-[#10B981]/20 text-[#10B981] font-bold text-xs flex items-center justify-center shrink-0">3</span>
            <span className="text-xs font-semibold text-[#FFFFFF]">Account Approval</span>
          </div>
          <div className="p-3 bg-[#09090B] rounded-xl border border-[#27272A] flex items-center space-x-3">
            <span className="w-6 h-6 rounded-full bg-[#3B82F6]/20 text-[#3B82F6] font-bold text-xs flex items-center justify-center shrink-0">4</span>
            <span className="text-xs font-semibold text-[#FFFFFF]">Trade Copier Active</span>
          </div>
        </div>
      </div>

      {feedbackMsg.text && (
        <div className={`p-4 rounded-2xl border text-xs font-semibold ${
          feedbackMsg.type === 'success' 
            ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' 
            : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
        }`}>
          {feedbackMsg.text}
        </div>
      )}

      {/* Submitted Accounts Table / Grid */}
      <Card title="Your Connected MT5 Accounts" subtitle="Real-time approval & trade copier status tracker">
        {loading ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">Loading connected accounts...</div>
        ) : accounts.length === 0 ? (
          <div className="p-8 text-center space-y-3">
            <Cpu className="w-10 h-10 text-[#9CA3AF]/40 mx-auto" />
            <p className="text-xs text-[#9CA3AF]">No MT5 accounts submitted yet.</p>
            <Button variant="secondary" size="sm" onClick={() => setShowSubmitModal(true)}>
              Submit Your First MT5 Account
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">Account ID</th>
                  <th className="py-3 px-4">Broker Server</th>
                  <th className="py-3 px-4">Account Type</th>
                  <th className="py-3 px-4">Approval Status</th>
                  <th className="py-3 px-4">xalgos Copier</th>
                  <th className="py-3 px-4">Closed PnL</th>
                  <th className="py-3 px-4">Submitted</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {accounts.map((acc) => (
                  <tr key={acc._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4 font-mono font-bold text-[#FFFFFF]">
                      {acc.accountNumber}
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {acc.server}
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {acc.accountType}
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge status={acc.status} label={acc.status} />
                      {acc.approvalNotes && (
                        <p className="text-[10px] text-[#9CA3AF] mt-1">Notes: {acc.approvalNotes}</p>
                      )}
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge 
                        status={acc.copierStatus === 'connected' ? 'approved' : acc.copierStatus === 'paused' ? 'pending' : 'disabled'} 
                        label={`Copier: ${acc.copierStatus}`} 
                      />
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#10B981]">
                      +${acc.totalClosedPnL ? acc.totalClosedPnL.toFixed(2) : '0.00'}
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {new Date(acc.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setAccountToDelete(acc)}
                        className="flex items-center space-x-1 ml-auto"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Delete</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Submission Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <h3 className="text-base font-extrabold text-[#FFFFFF]">Submit MT5 Account Details</h3>
              <button onClick={() => setShowSubmitModal(false)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">MT5 Account ID / Login</label>
                <div className="relative">
                  <Cpu className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                  <input
                    type="text"
                    required
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="e.g. 50129481"
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs font-mono focus:outline-none focus:border-[#F97316]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Broker Server Name</label>
                <div className="relative">
                  <Server className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                  <input
                    type="text"
                    required
                    value={server}
                    onChange={(e) => setServer(e.target.value)}
                    placeholder="e.g. MetaQuotes-Demo or IC markets-Live"
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Password</label>
                <div className="relative">
                  <Key className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                  <input
                    type="password"
                    required
                    value={investorPassword}
                    onChange={(e) => setInvestorPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-[#27272A]">
                <Button variant="secondary" size="sm" type="button" onClick={() => setShowSubmitModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit For Approval'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {accountToDelete && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <h3 className="text-base font-extrabold text-[#FFFFFF] flex items-center space-x-2">
                <Trash2 className="w-5 h-5 text-[#EF4444]" />
                <span>Confirm MT5 Account Deletion</span>
              </h3>
              <button onClick={() => setAccountToDelete(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <div className="space-y-2 text-xs text-[#9CA3AF]">
              <p>
                Are you sure you want to delete MT5 Account <span className="font-mono font-bold text-[#FFFFFF]">#{accountToDelete.accountNumber}</span>?
              </p>
              <div className="p-3 bg-[#09090B] rounded-xl border border-[#27272A] space-y-1">
                <p><span className="text-[#9CA3AF]">Broker Server:</span> <span className="font-semibold text-white">{accountToDelete.server}</span></p>
                <p><span className="text-[#9CA3AF]">Status:</span> <span className="font-semibold text-white capitalize">{accountToDelete.status}</span></p>
              </div>
              <p className="text-[#EF4444] font-semibold">
                ⚠️ This action cannot be undone. Copy trading for this account will be disconnected.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-3 border-t border-[#27272A]">
              <Button variant="secondary" size="sm" onClick={() => setAccountToDelete(null)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleDeleteAccount} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MT5Accounts;
